import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { AmplifyLogo } from "@/components/AmplifyLogo";
import { Eye, EyeOff, UserPlus, Loader2 } from "lucide-react";

export const RegisterForm = ({ onSwitchToLogin }) => {
  const { register, error, setError } = useAuth();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLocalError("");

    // Validate email domain
    if (!email.endsWith("@amplifyhrm.com")) {
      setLocalError("Registration requires an @amplifyhrm.com email address");
      return;
    }

    // Validate password match
    if (password !== confirmPassword) {
      setLocalError("Passwords do not match");
      return;
    }

    // Validate password strength
    if (password.length < 6) {
      setLocalError("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);
    const result = await register(email, password, fullName);
    setIsLoading(false);

    if (!result.success) {
      // Error is already set by register function
    }
  };

  const displayError = localError || error;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {/* Logo & Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <AmplifyLogo size={40} />
              <span className="text-2xl font-semibold font-['Outfit'] text-slate-900 tracking-tight">Amplify HR</span>
            </div>
            <h1 className="text-xl font-semibold text-slate-800">Create your account</h1>
            <p className="text-sm text-slate-500 mt-1">Join the Amplify HR Pricing Engine</p>
          </div>

          {/* Error Message */}
          {displayError && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600" data-testid="register-error">
              {displayError}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name</label>
              <input
                data-testid="register-name-input"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="John Doe"
                required
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c68ff] focus:ring-offset-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
              <input
                data-testid="register-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@amplifyhrm.com"
                required
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c68ff] focus:ring-offset-1"
              />
              <p className="text-xs text-slate-400 mt-1">Must be an @amplifyhrm.com email</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  data-testid="register-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  required
                  minLength={6}
                  className="w-full h-11 rounded-lg border border-slate-200 bg-white px-4 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c68ff] focus:ring-offset-1"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm Password</label>
              <input
                data-testid="register-confirm-password-input"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c68ff] focus:ring-offset-1"
              />
            </div>

            <button
              data-testid="register-submit-btn"
              type="submit"
              disabled={isLoading}
              className="w-full h-11 bg-[#3c68ff] text-white rounded-lg text-sm font-semibold hover:bg-[#2b4bca] transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  <UserPlus size={16} />
                  Create Account
                </>
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              Already have an account?{" "}
              <button
                data-testid="switch-to-login"
                onClick={onSwitchToLogin}
                className="text-[#3c68ff] font-medium hover:underline"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
